import React, { CSSProperties } from 'react';
import { AntButton } from 'controls/react/ant/antButton';
import { CaretDownOutlined, CaretUpOutlined, EditFilled } from '@ant-design/icons';
import { ToolbarItemPosition } from 'controls/react/layout/toolbar';
import { AntInput } from 'controls/react/ant/antInput';
import { CssVariables } from 'styles/cssVariables';

export function useMemoPermanent<T extends { destroy?: () => void }>(
	func: () => T,
	deps: ReadonlyArray<any> = null,
): T {
	const [value, setValue] = React.useState<T>();

	React.useEffect(() => {
		const innerValue = func();
		setValue(innerValue);
		return () => {
			innerValue?.destroy?.();
		};
	}, deps ?? []);

	return value;
}

export type UseEditableButtonOptions = {
	position?: ToolbarItemPosition;
	validator?: (newValue: string) => boolean;
	editable?: boolean;
	showIcon?: boolean;
};

export function useEditableButton<T extends object, K extends keyof T>(
	model: T,
	field: K,
	options?: UseEditableButtonOptions,
) {
	options ??= {};

	const [editing, setEditing] = React.useState(false);
	const [originalValue, setOriginalValue] = React.useState<T[K]>();
	const [tempValue, setTempValue] = React.useState<string>();

	const cancelEditing = React.useCallback(() => {
		setEditing(false);
	}, []);

	const startEditing = React.useCallback(() => {
		if (!model) return;

		setOriginalValue(model[field]);
		setTempValue(model[field] as string);
		setEditing(true);
	}, [model]);

	const confirmChange = React.useCallback(() => {
		if (options.validator) {
			if (!options.validator(model[field] as string)) return;
		}

		if (tempValue) {
			model[field] = tempValue as T[K];
		}

		setEditing(false);
	}, [originalValue, tempValue, model]);

	const editor = (
		<AntInput
			onPressEsc={cancelEditing}
			onBlur={confirmChange}
			autoFocus={true}
			onPressEnter={confirmChange}
			value={tempValue}
			onChange={(e) => setTempValue(e)}
			position={options.position}
		/>
	);

	const button = React.useMemo(() => {
		return <AntButton icon={<EditFilled />} type={'text'} onClick={startEditing} position={options.position} />;
	}, [startEditing]);

	const titleValue = (model?.[field] ?? '') as string;

	return {
		title: editing ? (
			editor
		) : (
			<>
				<span onClick={startEditing}>{titleValue}</span>
				{options.showIcon !== false && button}
			</>
		),
		startEditing: startEditing,
	};
}

export interface UseExpandCollapseButtonProps {
	expandTitle?: string;
	collapseTitle?: string;
}
export function useExpandCollapseButton(initialValue: boolean = false, options: UseExpandCollapseButtonProps = {}) {
	const [collapsed, setCollapsed] = React.useState(initialValue);

	const toggleCollapsed = React.useCallback(() => {
		setCollapsed(!collapsed);
	}, [collapsed]);

	const button = React.useMemo(() => {
		if (collapsed) {
			return (
				<AntButton
					icon={<CaretDownOutlined />}
					type={'text'}
					title={options.expandTitle}
					onClick={() => setCollapsed(false)}
				/>
			);
		} else {
			return (
				<AntButton
					icon={<CaretUpOutlined />}
					type={'text'}
					title={options.collapseTitle}
					onClick={() => setCollapsed(true)}
				/>
			);
		}
	}, [collapsed]);

	return [collapsed, button, toggleCollapsed] as const;
}

export function useDefaultZIndex(styles: CSSProperties, zIndex?: number) {
	return React.useMemo(() => {
		if (styles?.zIndex != null) {
			return styles;
		}

		return {
			...(styles ?? {}),
			zIndex: zIndex ?? CssVariables.zIndexControlPopover,
		};
	}, [styles, zIndex]);
}

export function isReactComponentType(x: any) {
	try {
		return React.isValidElement(React.createElement(x));
	} catch {
		return false;
	}
}
